/**
 * MyQuoteRequests.jsx
 *
 * Página para que los compradores vean todas sus solicitudes de cotización.
 */

import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
    Table,
    Tag,
    Button,
    Space,
    Typography,
    Card,
    Empty,
    Spin,
    message,
    Popconfirm,
    Tooltip,
    Layout,
    Dropdown
} from "antd"
import {
    EyeOutlined,
    DeleteOutlined,
    ReloadOutlined,
    ShoppingCartOutlined,
    InfoCircleOutlined,
    SendOutlined,
    MoreOutlined
} from "@ant-design/icons"
import { useAuth } from "../contexts/authContext"
import {
    getMyQuoteRequests,
    cancelQuoteRequest,
    resendQuoteRequestEmails,
    getQuoteRequestStatusInfo
} from "../services/QuoteRequestServices"
import "../assets/styles/MyQuoteRequests.css"

const { Content } = Layout
const { Text } = Typography

const MyQuoteRequests = ({ embedded = false }) => {
    const [quoteRequests, setQuoteRequests] = useState([])
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState({})
    const navigate = useNavigate()
    const { getToken } = useAuth()

    const fetchQuoteRequests = useCallback(async () => {
        try {
            setLoading(true)
            const token = await getToken()
            const data = await getMyQuoteRequests(token)
            setQuoteRequests(data.data || [])
        } catch (error) {
            console.error("Error fetching quote requests:", error)
            message.error("Error al cargar las solicitudes de cotización")
        } finally {
            setLoading(false)
        }
    }, [getToken])

    useEffect(() => {
        fetchQuoteRequests()
    }, [fetchQuoteRequests])

    const handleCancel = async (quoteRequestId) => {
        try {
            setActionLoading((prev) => ({ ...prev, [quoteRequestId]: "cancel" }))
            const token = await getToken()
            await cancelQuoteRequest(token, quoteRequestId)
            message.success("Solicitud cancelada")
            fetchQuoteRequests()
        } catch (error) {
            console.error("Error cancelling quote request:", error)
            message.error(error.message || "Error al cancelar la solicitud")
        } finally {
            setActionLoading((prev) => ({ ...prev, [quoteRequestId]: null }))
        }
    }

    const handleResendEmails = async (quoteRequestId) => {
        try {
            setActionLoading((prev) => ({ ...prev, [quoteRequestId]: "resend" }))
            const token = await getToken()
            await resendQuoteRequestEmails(token, quoteRequestId)
            message.success("Emails reenviados a los proveedores")
        } catch (error) {
            console.error("Error resending emails:", error)
            message.error(error.message || "Error al reenviar emails")
        } finally {
            setActionLoading((prev) => ({ ...prev, [quoteRequestId]: null }))
        }
    }

    const getResponsesCount = (request) => {
        if (!request.responses) return { responded: 0, total: 0 }
        const responses = Object.values(request.responses)
        const responded = responses.filter((r) => r.status === "responded").length
        return { responded, total: responses.length }
    }

    const columns = [
        {
            title: "Producto(s)",
            dataIndex: "original_product_name",
            key: "product",
            render: (text, record) => {
                const statusInfo = getQuoteRequestStatusInfo(record.status)
                const canCancel = ["pending", "sent", "partially_responded"].includes(record.status)
                return (
                <div>
                    <div className="quote-requests__product-header">
                        <Text strong className="quote-requests__product-name">
                            {text}
                        </Text>
                        <div className="quote-requests__product-actions-mobile">
                            <Dropdown
                                trigger={["click"]}
                                placement="bottomRight"
                                dropdownRender={() => (
                                    <div
                                        className="quote-requests__mobile-actions-menu"
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        <Button
                                            type="text"
                                            icon={<EyeOutlined />}
                                            onClick={(event) => {
                                                event.stopPropagation()
                                                navigate(`/mis-cotizaciones/${record.id}`)
                                            }}
                                        >
                                            Ver detalle
                                        </Button>
                                        {record.status === "sent" && (
                                            <Button
                                                type="text"
                                                icon={<SendOutlined />}
                                                loading={actionLoading[record.id] === "resend"}
                                                onClick={(event) => {
                                                    event.stopPropagation()
                                                    handleResendEmails(record.id)
                                                }}
                                            >
                                                Reenviar emails
                                            </Button>
                                        )}
                                        {canCancel && (
                                            <Popconfirm
                                                title="Cancelar solicitud?"
                                                description="Los proveedores ya no podran responder."
                                                onConfirm={() => handleCancel(record.id)}
                                                okText="Si, cancelar"
                                                cancelText="No"
                                            >
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    loading={actionLoading[record.id] === "cancel"}
                                                    onClick={(event) => {
                                                        event.stopPropagation()
                                                    }}
                                                >
                                                    Cancelar
                                                </Button>
                                            </Popconfirm>
                                        )}
                                    </div>
                                )}
                            >
                                <Button
                                    size="small"
                                    className="quote-requests__mobile-actions-trigger"
                                    icon={<MoreOutlined />}
                                    onClick={(event) => event.stopPropagation()}
                                />
                            </Dropdown>
                        </div>
                    </div>
                    <div className="quote-requests__product-status-mobile">
                        <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
                    </div>
                    <br />
                    {record.type === "cart_request" ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {record.total_products} producto(s) del carrito
                        </Text>
                    ) : (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {record.original_product_category}
                        </Text>
                    )}
                    <div className="quote-requests__product-meta">
                        <span className="quote-requests__meta-item">
                            {record.type === "cart_request"
                                ? `Cantidad: ${record.total_quantity || record.quantity}`
                                : `Cantidad: ${record.quantity}`}
                        </span>
                        <span className="quote-requests__meta-item">
                            {(() => {
                                const { responded, total } = getResponsesCount(record)
                                return `${record.detected_providers?.length || 0} prov. • ${responded}/${total} resp.`
                            })()}
                        </span>
                        <span className="quote-requests__meta-item">
                            {new Date(record.created_at).toLocaleDateString("es-AR")}
                        </span>
                    </div>
                </div>
            )}
        },
        {
            title: "Cantidad",
            dataIndex: "quantity",
            key: "quantity",
            width: 100,
            align: "center",
            responsive: ["md"],
            render: (quantity, record) => (
                <span>
                    {record.type === "cart_request"
                        ? record.total_quantity || quantity
                        : quantity}
                </span>
            )
        },
        {
            title: "Proveedores",
            key: "providers",
            width: 150,
            responsive: ["md"],
            render: (_, record) => {
                const { responded, total } = getResponsesCount(record)
                return (
                    <div>
                        <Text>{record.detected_providers?.length || 0} detectados</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {responded}/{total} respondieron
                        </Text>
                    </div>
                )
            }
        },
        {
            title: "Estado",
            dataIndex: "status",
            key: "status",
            width: 150,
            responsive: ["md"],
            render: (status) => {
                const statusInfo = getQuoteRequestStatusInfo(status)
                return <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
            }
        },
        {
            title: "Fecha",
            dataIndex: "created_at",
            key: "created_at",
            width: 120,
            responsive: ["md"],
            render: (date) => new Date(date).toLocaleDateString("es-AR")
        },
        {
            title: "Acciones",
            key: "actions",
            width: 200,
            responsive: ["md"],
            render: (_, record) => (
                <Space>
                    <Tooltip title="Ver detalles">
                        <Button
                            type="primary"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={(event) => {
                                event.stopPropagation()
                                navigate(`/mis-cotizaciones/${record.id}`)
                            }}
                        />
                    </Tooltip>
                    {record.status === "sent" && (
                        <Tooltip title="Reenviar emails">
                            <Button
                                size="small"
                                icon={<SendOutlined />}
                                loading={actionLoading[record.id] === "resend"}
                                onClick={(event) => {
                                    event.stopPropagation()
                                    handleResendEmails(record.id)
                                }}
                            />
                        </Tooltip>
                    )}
                    {["pending", "sent", "partially_responded"].includes(record.status) && (
                        <Popconfirm
                            title="¿Cancelar solicitud?"
                            description="Los proveedores ya no podrán responder."
                            onConfirm={() => handleCancel(record.id)}
                            okText="Sí, cancelar"
                            cancelText="No"
                        >
                            <Tooltip title="Cancelar solicitud">
                                <Button
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    loading={actionLoading[record.id] === "cancel"}
                                    onClick={(event) => {
                                        event.stopPropagation()
                                    }}
                                />
                            </Tooltip>
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ]

    const tableTitle = !embedded ? (
        <div className="quote-requests__table-title">
            <span>Solicitudes de Cotización</span>
            <Tooltip title="Hacé click en una fila para ver el detalle y el análisis.">
                <InfoCircleOutlined className="quote-requests__table-info" />
            </Tooltip>
        </div>
    ) : null

    const content = (
        <>
            <div className={embedded ? "quote-requests__header-embedded" : "quote-requests__header"}>
                <div>
                    {!embedded && <p className="quote-requests__eyebrow">Gestión de cotizaciones</p>}
                    <h1 className={embedded ? "quote-requests__title-embedded" : "quote-requests__title"}>
                        Mis Solicitudes de Cotización
                    </h1>
                    {!embedded && (
                        <p className="quote-requests__subtitle">
                            Revisa el estado de tus solicitudes enviadas a proveedores y compara las cotizaciones recibidas.
                        </p>
                    )}
                </div>
                <div className="quote-requests__actions">
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchQuoteRequests}
                        loading={loading}
                    >
                        Actualizar
                    </Button>
                    {!embedded && (
                        <Button
                            type="primary"
                            icon={<ShoppingCartOutlined />}
                            onClick={() => navigate("/cart")}
                        >
                            Ir al Carrito
                        </Button>
                    )}
                </div>
            </div>

            <div className="quote-requests__content">
                <Card
                    className={embedded ? "quote-requests__card-embedded" : "quote-requests__card"}
                    title={tableTitle}
                >
                    {loading ? (
                        <div style={{ textAlign: "center", padding: embedded ? 32 : 64 }}>
                            <Spin size="large" />
                            <p style={{ marginTop: 16, color: "#666" }}>Cargando solicitudes...</p>
                        </div>
                    ) : quoteRequests.length === 0 ? (
                        <div className="quote-requests__empty">
                            <Empty
                                description="No tienes solicitudes de cotización"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            >
                                <Button
                                    type="primary"
                                    icon={<ShoppingCartOutlined />}
                                    onClick={() => navigate("/cart")}
                                >
                                    Solicitar Cotización desde el Carrito
                                </Button>
                            </Empty>
                        </div>
                    ) : (
                        <Table
                            className="quote-requests__table"
                            dataSource={quoteRequests}
                            columns={columns}
                            rowKey="id"
                            onRow={(record) => ({
                                onClick: () => navigate(`/mis-cotizaciones/${record.id}`)
                            })}
                            pagination={{
                                pageSize: embedded ? 5 : 10,
                                showSizeChanger: !embedded,
                                showTotal: (total) => `${total} solicitudes`
                            }}
                            size={embedded ? "small" : "middle"}
                        />
                    )}
                </Card>
            </div>
        </>
    )

    if (embedded) {
        return <div className="quote-requests__embedded">{content}</div>
    }

    return (
        <Layout className="quote-requests__page">
            <Content className="quote-requests__layout-inner">
                {content}
            </Content>
        </Layout>
    )
}

export default MyQuoteRequests
